import styled from '@emotion/styled';
import { CircularProgress } from '@material-ui/core';
import { Box } from 'components';
import { Link as RouterLink } from '@reach/router';

import {
  GameDifficulty,
  GameTypeEnum,
  MeWithTutorialQuery_me_company_gamesConfig,
  SceneStatusEnum,
  CompanyTraining,
  CompanyTrainingVariables,
} from 'graphql/types';
import { useQuery } from '@apollo/client';
import { ThemeContext } from '@emotion/react';
import { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AppTheme } from 'theme';
import colors from 'lib/colors';

import { ScenesProgressContext } from 'pages/Lobby/ScenesProgressProvider';

import useInvitations from '../../hooks/useInvitations';
import { companyTraining } from 'pages/Lobby/operations';

import ScenesProgressHeading from '../ScenesProgressHeading';
import InvitationList from './InvitationList/InvitationList';
import MatchmakingButton from './MatchmakingButton/MatchmakingButton';
import SceneList from './SceneList/SceneList';
import TrainingLegend, { Legend } from './TrainingLegend';
import MatchViewFilter from './MatchViewFilter';
import MatchViewMessages from './MatchViewMessages';

import SceneDetailModal from '../SceneDetailModal';

export enum SceneStatusFilterAll {
  ALL = 'ALL',
}

export type SceneStatusFilterEnum = SceneStatusEnum | SceneStatusFilterAll;

export interface MatchViewProps {
  gameType: GameTypeEnum;
  gameDifficulty: GameDifficulty;
  gamesConfig?: MeWithTutorialQuery_me_company_gamesConfig[];
  tutorialSceneName?: string;
  opponentCount?: number;
  trainingId?: string;
  isMediumGameActive?: boolean;
}

const MatchView = ({
  gameType, // NOTE: Not used in new (EDU) layout
  gameDifficulty,
  gamesConfig,
  isMediumGameActive,
  opponentCount,
  tutorialSceneName,
  trainingId,
}: MatchViewProps) => {
  const { t } = useTranslation();
  const {
    currentScenes,
    attendees,
    isActiveTraining,
    freeGameScenes,
    trainingScenes,
    tournamentScenes,
    firstTimeLoading: loading,
  } = useContext(ScenesProgressContext);

  const { data: companyTrainingData } = useQuery<
    CompanyTraining,
    CompanyTrainingVariables
  >(companyTraining, {
    variables: {
      input: { uuid: trainingId! },
    },
    skip: !trainingId,
  });

  // NOTE: Used in default old layout
  const isTournamentGame = gameType === GameTypeEnum.TOURNAMENT_GAME;
  const isTrainingGame = useMemo(
    () => gameType === GameTypeEnum.TRAINING_GAME,
    [gameType],
  );

  const { layout } = useContext(ThemeContext) as AppTheme;
  const IS_EDU = !!layout.edu;

  const getScenesByGameType = (gameType: GameTypeEnum) => {
    if (IS_EDU) {
      return currentScenes;
    }

    switch (gameType) {
      case GameTypeEnum.FREE_GAME:
        return freeGameScenes;
      case GameTypeEnum.TOURNAMENT_GAME:
        return tournamentScenes;
      default:
        return trainingScenes;
    }
  };

  const invitationState = useInvitations(gameType);
  const [selectedSceneId, setSelectedSceneId] = useState<string | undefined>(
    undefined,
  );
  const [openSceneDetailModal, setOpenSceneDetailModal] =
    useState<boolean>(false);

  const ALL_SCENES = getScenesByGameType(gameType);

  const [scenes, setScenes] = useState(ALL_SCENES);

  useEffect(() => {
    // if trainingId & companyTrainingData exists
    if (companyTrainingData) {
      // filter scenes according to selected training content
      const sceneIds: string[] = (
        companyTrainingData.companyTraining?.scenes || []
      ).map((scene) => scene.id);
      const filteredScenes = ALL_SCENES.filter((scene) =>
        sceneIds.includes(scene.id),
      );
      setScenes(filteredScenes);
    } else {
      setScenes(ALL_SCENES);
    }
  }, [companyTrainingData, ALL_SCENES]);

  const isTraining = useCallback(() => {
    return !!(scenes || []).find(
      (scene) => (scene.statuses || []).indexOf(SceneStatusEnum.TRAINING) > -1,
    );
  }, [scenes]);

  const isTournament = useCallback(() => {
    return !!(scenes || []).find(
      (scene) =>
        (scene.statuses || []).indexOf(SceneStatusEnum.TOURNAMENT) > -1,
    );
  }, [scenes]);

  useEffect(() => {
    const currentState = isTournament()
      ? SceneStatusEnum.TOURNAMENT
      : isTraining()
        ? SceneStatusEnum.TRAINING
        : SceneStatusFilterAll.ALL;

    setSelectedFilter(currentState);
  }, [currentScenes, isTraining, isTournament]);

  const [selectedFilter, setSelectedFilter] = useState<SceneStatusFilterEnum>(
    () => (isTraining() ? SceneStatusEnum.TRAINING : SceneStatusFilterAll.ALL),
  );

  const toggleScene = (newSceneId?: string) => {
    if (newSceneId === selectedSceneId) {
      setSelectedSceneId(undefined);
    } else {
      setSelectedSceneId(newSceneId);
    }
  };

  const handleOpenScene = (newSceneId?: string) => {
    const selectedScene = scenes.find((scene) => scene.id === newSceneId);
    setSelectedSceneId(selectedScene?.id);
    setOpenSceneDetailModal(true);
  };

  const handleSceneDetailModalClose = () => {
    setOpenSceneDetailModal(false);
  };

  const handleMatchViewFilterChange = (status: SceneStatusFilterEnum) => {
    setSelectedFilter(status);
  };

  const noScene = scenes.length === 0;

  if (loading) {
    return (
      <CircularProgress
        size={24}
        color="inherit"
        css={{ margin: '50px auto', color: colors.lobbyOrange }}
      />
    );
  }

  return (
    <div>
      {IS_EDU && (
        <MatchViewMessages
          attendees={attendees}
          isActiveTraining={!!isActiveTraining}
          opponentCount={opponentCount}
        />
      )}

      {IS_EDU && (
        <MatchViewFilter
          selectedFilter={selectedFilter}
          scenes={scenes}
          onFilter={handleMatchViewFilterChange}
        />
      )}

      {isMediumGameActive && <InvitationList invitations={invitationState} />}

      {companyTrainingData?.companyTraining && (
        <div css={{ margin: '20px 0' }}>
          {t('matchView.scenesFromCurrentTraining', {
            defaultValue: 'Zobrazeny dějiště z vybraného školení {{name}}.',
            name: companyTrainingData.companyTraining.name,
          })}{' '}
          <Link to={'/training'}>{t('matchView.showAll', 'Zobrazit vše')}</Link>
        </div>
      )}

      <MatchViewContainer>
        <Content>
          {!noScene && !isTournamentGame && (
            <ScenesProgressHeading
              gameType={gameType}
              gamesConfig={gamesConfig}
            />
          )}
          {!noScene ? (
            <>
              <SceneList
                scenes={scenes}
                selectedFilter={selectedFilter}
                selectedSceneId={selectedSceneId}
                handleSceneCLick={IS_EDU ? handleOpenScene : toggleScene}
                gameDifficulty={gameDifficulty}
                showEmptyProgress={isTournamentGame}
              />

              <Box display="flex" css={{ marginBottom: '32px' }}>
                {isTrainingGame ? <TrainingLegend /> : <Legend>&nbsp;</Legend>}
              </Box>

              {!IS_EDU && (
                <MatchmakingButton
                  selectedSceneId={selectedSceneId}
                  disabled={
                    invitationState.invitations != null &&
                    invitationState.invitations.length > 0
                  }
                  gameType={gameType}
                  gameDifficulty={gameDifficulty}
                  isMediumGameActive={isMediumGameActive}
                />
              )}
            </>
          ) : !IS_EDU ? (
            <Legend css={{ color: 'white', textAlign: 'center' }}>
              {gameType === GameTypeEnum.TOURNAMENT_GAME
                ? t(
                  'matchView.noPlayableTournaments',
                  'Nejste přiřazeni do žádného turnaje',
                )
                : t(
                  'matchView.noPlayableScenes',
                  'Nejste přiřazen do žádného školení',
                )}
            </Legend>
          ) : (
            <CircularProgress
              size={30}
              color="inherit"
              css={{ margin: '50px auto', color: colors.lobbyOrange }}
            />
          )}
        </Content>
      </MatchViewContainer>

      {IS_EDU && (
        <SceneDetailModal
          isOpen={!!openSceneDetailModal}
          isPlayButtonDisabled={
            invitationState.invitations != null &&
            invitationState.invitations.length > 0
          }
          scene={scenes.find((scene) => scene.id === selectedSceneId)!}
          tutorialSceneName={tutorialSceneName}
          gameType={gameType}
          gameDifficulty={gameDifficulty}
          gamesConfig={gamesConfig}
          onClose={handleSceneDetailModalClose}
        />
      )}
    </div>
  );
};

export default MatchView;

const MatchViewContainer = styled('div')({
  marginTop: 64,
  '@media screen and (max-width: 1000px)': {
    marginTop: 32,
  },
});

const Content = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: white;
  margin-bottom: 1em;
`;

const Link = styled(RouterLink)`
  font-weight: 500;
  letter-spacing: 0;
  color: ${colors.primary};
  text-decoration: none;
`;
